import React, { useState, useEffect, useCallback } from "react";
import { useLoadScript } from "@react-google-maps/api";
import DashboardLayout from "../../../components/common/DashboardLayout";
import ArtistCard from "../components/groups/ArtistCard";
import BarCard from "../components/groups/BarCard";
import ArtistProfileModal from "../components/groups/ArtistProfileModal";
import BarModal from "../components/groups/BarModal";
import CreateArtistModal from "../components/groups/CreateArtistModal";
import { useArtist } from "../../../context/ArtistContext";

const libraries = ["places"];

const Groups = () => {
  const {
    userArtists: artists,
    loading: artistsLoading,
    error: artistsError,
    refreshArtists,
    groupsBars: bars,
    groupsBarsLoading: barsLoading,
    groupsBarsError: barsError,
    fetchGroupsBars,
    refreshGroupsBars,
  } = useArtist();

  const [selectedArtist, setSelectedArtist] = useState(null);
  const [selectedBar, setSelectedBar] = useState(null);
  const [isArtistModalOpen, setIsArtistModalOpen] = useState(false);
  const [isBarModalOpen, setIsBarModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Load Google Maps API once at the page level
  const { isLoaded: isMapsLoaded, loadError: mapsLoadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: libraries,
  });

  // Fetch bars on mount (uses context cache; no refetch when navigating back)
  useEffect(() => {
    fetchGroupsBars();
  }, [fetchGroupsBars]);

  // Listen for artist member updates (from invite acceptance, etc.)
  useEffect(() => {
    const handleArtistMembersUpdated = () => {
      refreshArtists();
      refreshGroupsBars();
    };

    window.addEventListener("artistMembersUpdated", handleArtistMembersUpdated);
    return () => {
      window.removeEventListener(
        "artistMembersUpdated",
        handleArtistMembersUpdated,
      );
    };
  }, [refreshArtists, refreshGroupsBars]);

  const handleArtistClick = (artist) => {
    setSelectedArtist(artist);
    setIsArtistModalOpen(true);
  };

  const handleBarClick = (bar) => {
    setSelectedBar(bar);
    setIsBarModalOpen(true);
  };

  const handleCloseArtistModal = () => {
    setIsArtistModalOpen(false);
    setSelectedArtist(null);
  };

  const handleCloseBarModal = () => {
    setIsBarModalOpen(false);
    setSelectedBar(null);
  };

  const handleCreateSuccess = useCallback(async () => {
    await refreshArtists();
    await refreshGroupsBars();
    window.dispatchEvent(new CustomEvent("artistMembersUpdated"));
  }, [refreshArtists, refreshGroupsBars]);

  return (
    <DashboardLayout>
      <div className="groups-page">
        {/* My Groups Section */}
        <div className="groups-section">
          <div className="groups-page-header">
            <button
              type="button"
              className="btn btn-primary groups-page-header__action"
              onClick={() => setIsCreateModalOpen(true)}
            >
              Create Artist
            </button>
            <h2 className="section-title groups-page-header__title">
              My Groups
            </h2>
          </div>
          {artistsLoading ? (
            <div className="groups-loading">
              <div className="spinner"></div>
              <p>Loading artists...</p>
            </div>
          ) : artistsError ? (
            <div className="groups-error">
              <p>{artistsError}</p>
            </div>
          ) : !artists || artists.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">👥</div>
              <h3>No Artists Found</h3>
              <p>You are not associated with any artists yet.</p>
            </div>
          ) : (
            <div className="artists-grid">
              {artists.map((artist) => (
                <ArtistCard
                  key={artist.artist_id}
                  artist={artist}
                  onClick={() => handleArtistClick(artist)}
                />
              ))}
            </div>
          )}
        </div>

        {/* My Bars Section */}
        <div className="groups-section">
          <h2 className="section-title">My Bars</h2>
          {barsLoading ? (
            <div className="groups-loading">
              <div className="spinner"></div>
              <p>Loading bars...</p>
            </div>
          ) : barsError ? (
            <div className="groups-error">
              <p>{barsError}</p>
            </div>
          ) : !bars || bars.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🏪</div>
              <h3>No Bars Found</h3>
              <p>None of your artists are associated with any bars yet.</p>
            </div>
          ) : (
            <div className="bars-grid">
              {bars.map((bar) => (
                <BarCard
                  key={bar.bar_id}
                  bar={bar}
                  onClick={() => handleBarClick(bar)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Modals */}
        {selectedArtist && (
          <ArtistProfileModal
            isOpen={isArtistModalOpen}
            onClose={handleCloseArtistModal}
            artist={selectedArtist}
            isMapsLoaded={isMapsLoaded}
            onArtistUpdate={(updatedArtist) => {
              setSelectedArtist(updatedArtist);
              refreshArtists();
            }}
            onArtistDeleted={(deletedArtistId) => {
              if (selectedArtist?.artist_id === deletedArtistId) {
                handleCloseArtistModal();
              }
              refreshArtists();
            }}
            onMembersChanged={() => {
              refreshArtists();
            }}
          />
        )}

        {selectedBar && (
          <BarModal
            isOpen={isBarModalOpen}
            onClose={handleCloseBarModal}
            bar={selectedBar}
            isMapsLoaded={isMapsLoaded}
          />
        )}

        {/* Create Artist Modal */}
        <CreateArtistModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          isMapsLoaded={isMapsLoaded}
        />
      </div>
    </DashboardLayout>
  );
};

export default Groups;
